import { Component, ElementRef, HostListener, OnInit } from "@angular/core";
import { Chart, registerables } from "chart.js";
import { AnalyticsService } from "../../services/analytics-data.service";
import { takeUntil } from "rxjs";
import { UnsubscribeService } from "src/app/services/unsubscribe.service";
import { DateState, SelectMonthUnit } from "../../models/analytics.model";
import { STYLES } from "../../constants/analytics.constant";
import { TranslateService } from "@ngx-translate/core";
import { AnalyticsTranslationService } from "../../services/analytics-translation.service";

Chart.register(...registerables);

@Component({
	selector: "app-payment-page-analytics",
	templateUrl: "./payment-page-analytics.component.html",
	styleUrls: ["./payment-page-analytics.component.scss"],
	providers: [UnsubscribeService],
})
export class PaymentPageAnalyticsComponent implements OnInit {
	chart: any;
	data: any;

	labelsChartStructure: Array<string> = [];
	hitsChartStructure: Array<number> = [];
	successfulTransactionsChartStructure: Array<number> = [];

	totalHits = 0;
	totalSuccessfulTransactions = 0;

	isOpenMonthSelect = false;
	monthList: Array<SelectMonthUnit> = [];
	selectedMonth: SelectMonthUnit | null = null;

	isOpenYearSelect = false;
	yearList: Array<number> = [];
	selectedYear: number | null = null;

	isDataValid = true;

	constructor(
		private analyticsService: AnalyticsService,
		private unsubscribe$: UnsubscribeService,
		private translateService: TranslateService,
		private analyticsTranslationService: AnalyticsTranslationService,
		private el: ElementRef
	) {}

	ngOnInit() {
		this.setMonthList();
		this.setYearList();
		this.initDateValues();
		this.loadInfo(true);

		this.checkLangChange();
	}

	initDateValues() {
		const currentDate = new Date();
		const currentMonth = currentDate.getMonth() + 1;
		this.selectedYear = currentDate.getFullYear();
		this.selectedMonth = this.monthList.find((month) => month.value === currentMonth) || null;
	}

	openMonthList(event: MouseEvent) {
		event.stopPropagation();
		this.isOpenYearSelect = false;
		this.isOpenMonthSelect = true;
	}

	setMonth(event: MouseEvent, month: SelectMonthUnit) {
		event.stopPropagation();
		this.selectedMonth = month;
		this.isOpenMonthSelect = false;
		this.loadInfo();
	}

	openYearList(event: MouseEvent) {
		event.stopPropagation();
		this.isOpenMonthSelect = false;
		this.isOpenYearSelect = true;
	}

	setYear(event: MouseEvent, year: number) {
		event.stopPropagation();
		this.selectedYear = year;
		this.isOpenYearSelect = false;
		this.loadInfo();
	}

	@HostListener("document:click", ["$event"])
	onClick(event: Event) {
		if (this.el.nativeElement.parentNode.parentNode.parentNode.contains(event.target)) {
			this.isOpenMonthSelect = false;
			this.isOpenYearSelect = false;
		}
	}

	setMonthList() {
		const monthLabels = this.translateService.instant("ANALYTICS_PAGE.MONTH_LABELS");
		this.monthList = Object.keys(monthLabels).map((fieldName, index) => ({
			fieldName: monthLabels[fieldName],
			value: index + 1,
		}));
	}

	setYearList() {
		const currentYear = new Date().getFullYear();
		this.yearList = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1, currentYear + 2];
	}

	renderChar() {
		this.data = this.initializeChartData();

		const hoverSegment = {
			hoverValue: null as null | number,
			id: "hoverSegment",
			beforeDatasetsDraw(chart: any) {
				const {
					ctx,
					chartArea: { top, width, height },
					scales: { x },
				} = chart;
				ctx.save();

				const segment = width / (x.max + 1);

				ctx.fillStyle = "rgba(173, 216, 230, 0.2)";

				if (this.hoverValue || this.hoverValue === 0) {
					ctx.fillRect(x.getPixelForValue(this.hoverValue) - segment / 2, top, segment, height);
				}
			},
			afterEvent(chart: any, args: any) {
				const {
					scales: { x },
				} = chart;

				if (args.inChartArea) {
					this.hoverValue = x.getValueForPixel(args.event.x);
				} else {
					this.hoverValue = null;
				}

				args.changed = true;
			},
		};

		this.chart = new Chart("return-page", {
			type: "bar",
			data: this.data,
			plugins: [hoverSegment],
			options: {
				interaction: {
					mode: "index",
					intersect: false,
				},
				plugins: {
					title: {
						display: false,
					},
					legend: {
						display: false,
						position: "bottom",
					},
					tooltip: {
						enabled: false,
						external: (context) => {
							let tooltipEl = document.getElementById("chartjs-tooltip");

							if (!tooltipEl) {
								tooltipEl = document.createElement("div");
								tooltipEl.id = "chartjs-tooltip";
								tooltipEl.innerHTML = "<table></table>";
								document.body.appendChild(tooltipEl);
							}

							const tooltipModel = context.tooltip;

							if (tooltipModel.opacity === 0) {
								tooltipEl.style.opacity = "0";
								return;
							}

							if (tooltipModel.opacity === 0 || allDataZero()) {
								tooltipEl.style.opacity = "0";
								return;
							}

							function allDataZero() {
								const bodyLines = tooltipModel.body.map(getBody);
								for (let i = 0; i < bodyLines.length; i++) {
									if (Math.ceil(bodyLines[i][0].split(":")[1]) !== 0) {
										return false;
									}
								}
								return true;
							}

							tooltipEl.classList.remove("above", "below", "no-transform");
							if (tooltipModel.yAlign) {
								tooltipEl.classList.add(tooltipModel.yAlign);
							} else {
								tooltipEl.classList.add("no-transform");
							}

							function getBody(bodyItem: any) {
								return bodyItem.lines;
							}

							if (tooltipModel.body) {
								const bodyLines = tooltipModel.body.map(getBody);
								let innerHtml = "";

								bodyLines.forEach((body, i) => {
									let description = body[0].split(":")[0];

									description = this.translateService.instant(
										`ANALYTICS_PAGE.${description === "Всего совершено успешных платежей" ? "BY_TRANSACTIONS" : "BY_PAYMENTS"}`
									);

									const value = body[0].split(":")[1];
									const colors = tooltipModel.labelColors[i];
									innerHtml += `                  <tbody>
                    <tr>
                      <td>
                        <div style="${STYLES.div.flexBetween}">
                          <div style="${STYLES.div.flex} ${STYLES.gap(4)} ${STYLES.div.alignCenter}">
                            <div style="${STYLES.width(8)} ${STYLES.height(8)} ${STYLES.borderRadius(100)} background-color: ${
															colors.backgroundColor
														}; border: 1px solid ${colors.borderColor};"></div>
                            <span style="${STYLES.font.common} ${STYLES.font.normal}"> ${description}</span>
                          </div>
                          <span style="${STYLES.font.common} ${STYLES.font.bold}"> ${value}</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>`;
								});

								(tooltipEl.querySelector("table") as HTMLTableElement).innerHTML = innerHtml;
							}

							const position = context.chart.canvas.getBoundingClientRect();

							const tooltipStyle = tooltipEl.style;

							Object.assign(tooltipStyle, {
								backgroundColor: "#FFF",
								width: "150px",
								padding: "8px",
								position: "absolute",
								opacity: "1",
								transition: "0.3s",
								display: "flex",
								flexDirection: "column",
								gap: "4px",
								borderRadius: "6px",
								boxShadow: "0px 4px 16px 0px rgba(112, 112, 112, 0.25)",
								left: position.left + window.scrollX + tooltipModel.caretX + 24 + "px",
								top: position.top + window.scrollY + tooltipModel.caretY + "px",
								pointerEvents: "none",
							});
						},
					},
				},
				responsive: true,
				maintainAspectRatio: false,
				scales: {
					x: {},
					y: {
						border: {
							dash: [3, 4],
						},
					},
				},
			},
		});
	}

	private initializeChartData() {
		const data = {
			labels: this.labelsChartStructure,
			datasets: [
				{
					label: "Всего совершено успешных платежей",
					data: this.hitsChartStructure,
					backgroundColor: ["rgba(76, 113, 239, 1)"],
					radius: 145,
					hoverOffset: 30,
					offset: 4,
					borderRadius: 6,
					borderSkipped: false,
					spacing: 8,
				},
				{
					label: "Всего переходов",
					data: this.successfulTransactionsChartStructure,
					backgroundColor: ["rgba(59, 187, 147, 1)"],
					radius: 145,
					hoverOffset: 30,
					offset: 4,
					borderRadius: 6,
					borderSkipped: false,
					spacing: 8,
				},
			],
		};
		return data;
	}

	private loadInfo(isFirstCall: boolean = false) {
		this.analyticsService
			.getPaymentPageAnalytics(this.selectedYear!, this.selectedMonth!.value)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				next: (res) => {
					this.resetChartValues();

					this.totalHits = res.total_hits;
					this.totalSuccessfulTransactions = res.total_successful_transactions;

					if (this.totalHits <= 0 && this.totalSuccessfulTransactions <= 0) {
						this.isDataValid = false;
					} else {
						this.isDataValid = true;
					}
					res.payment_page_statistics.map((el) => this.hitsChartStructure.push(el.hits_counter));

					res.payment_page_statistics.map((el) => this.successfulTransactionsChartStructure.push(el.successful_transactions_counter));

					const chartLabels: Array<string> = [];

					res.payment_page_statistics.map((el) => chartLabels.push(el.month));

					this.labelsChartStructure = this.analyticsTranslationService.mapDatesToLabels(chartLabels, DateState.Year) as string[];

					if (isFirstCall) {
						this.renderChar();
					} else {
						this.data.datasets = this.initializeChartData().datasets;
						this.data.labels = this.initializeChartData().labels;
						this.chart.update();
					}
				},
			});
	}

	private resetChartValues() {
		this.hitsChartStructure = [];
		this.successfulTransactionsChartStructure = [];
		this.labelsChartStructure = [];
	}

	private checkLangChange() {
		this.translateService.onLangChange.pipe(takeUntil(this.unsubscribe$)).subscribe({
			next: () => {
				this.setMonthList();
				this.initDateValues();
				this.loadInfo();
			},
		});
	}
}
